"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/pitchbuddy-public-speaking/id6752858863";
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.ali.pitch_buddy";

export default function FinalCtaSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setIsVisible(true);
          observer.disconnect();
          break;
        }
      },
      { threshold: 0.3, rootMargin: "0px 0px -8% 0px" }
    );

    const sectionNode = sectionRef.current;
    if (sectionNode) observer.observe(sectionNode);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="download"
      className="pt-10 pb-14 sm:pt-12 sm:pb-16 lg:pt-14 lg:pb-20"
      aria-labelledby="final-cta-heading"
    >
      <div
        className={`relative left-1/2 w-screen -translate-x-1/2 overflow-hidden bg-gradient-to-br from-[#fffaf4] via-[#fff5e9] to-[#ffeccd] px-6 py-14 text-center shadow-[0_0_0_1px_rgba(245,158,11,0.16),0_22px_56px_rgba(30,26,36,0.13)] transition-all duration-700 ease-out sm:px-10 sm:py-16 lg:py-18 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
        }`}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 motion-reduce:animate-none animate-[ctaMeshDrift_24s_ease-in-out_infinite] bg-[radial-gradient(60%_60%_at_18%_30%,rgba(245,158,11,0.22),transparent_55%),radial-gradient(55%_55%_at_82%_22%,rgba(252,211,77,0.20),transparent_60%),radial-gradient(70%_70%_at_50%_95%,rgba(245,158,11,0.18),transparent_62%)] [background-size:160%_160%]"
        />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 motion-reduce:animate-none animate-[ctaSpotlight_18s_ease-in-out_infinite] bg-[radial-gradient(28%_36%_at_50%_30%,rgba(255,255,255,0.65),transparent_70%)]"
        />

        <div
          aria-hidden="true"
          className={`pointer-events-none absolute -left-20 top-12 h-60 w-60 rounded-full bg-primary/22 blur-3xl transition-all duration-1000 ease-out motion-reduce:animate-none ${
            isVisible ? "scale-100 opacity-100" : "scale-90 opacity-70"
          } animate-[ctaFloatLeft_16s_ease-in-out_infinite]`}
        />
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute -bottom-20 -right-10 h-64 w-64 rounded-full bg-[#fcd34d]/30 blur-3xl transition-all duration-1000 ease-out motion-reduce:animate-none ${
            isVisible ? "scale-100 opacity-100" : "scale-90 opacity-70"
          } animate-[ctaFloatRight_18s_ease-in-out_infinite]`}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-1/4 top-6 h-32 w-32 rounded-full bg-primary/18 blur-3xl motion-reduce:animate-none animate-[ctaFloatRight_22s_ease-in-out_infinite]"
        />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/35 to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
        />

        <div className="relative mx-auto max-w-5xl">
          <span
            className={`relative inline-flex items-center gap-2 rounded-full border border-primary/25 bg-white/65 px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-primary/90 shadow-[0_4px_14px_rgba(245,158,11,0.16)] backdrop-blur-sm transition-all duration-500 ease-out ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "-translate-y-1 opacity-0"
            }`}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/55 opacity-70 motion-reduce:hidden" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Free to download
          </span>

          <h2
            id="final-cta-heading"
            className={`mx-auto mt-4 max-w-3xl text-[1.6rem] font-extrabold leading-tight tracking-tight text-foreground transition-all duration-700 ease-out sm:text-[2.1rem] ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-2 opacity-0"
            }`}
          >
            Start practicing with PitchBuddy
          </h2>
          <p
            className={`mx-auto mt-3 max-w-xl text-[0.95rem] leading-6 text-foreground/68 transition-all duration-700 ease-out sm:text-base sm:leading-7 ${
              isVisible
                ? "translate-y-0 opacity-100 delay-100"
                : "translate-y-2 opacity-0"
            }`}
          >
            Download the app, practice out loud, and get instant feedback.
          </p>

          <div
            className={`relative mx-auto mt-7 max-w-xl transition-all duration-700 ease-out motion-reduce:animate-none sm:mt-8 ${
              isVisible
                ? "translate-y-0 scale-100 opacity-100 delay-150"
                : "translate-y-2 scale-[0.98] opacity-0"
            } animate-[ctaPhoneFloat_10s_ease-in-out_infinite]`}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 -z-10 mx-auto h-full w-[78%] rounded-full bg-[radial-gradient(closest-side,rgba(245,158,11,0.32),transparent_72%)] blur-2xl motion-reduce:animate-none animate-[ctaHaloPulse_5.5s_ease-in-out_infinite] sm:w-[72%] lg:w-[68%]"
            />
            <Image
              src="/images/cta/cta-phone-stack.webp"
              alt="PitchBuddy phone screens showing practice, insights, and progress"
              width={1080}
              height={1080}
              sizes="(max-width: 640px) 78vw, (max-width: 1024px) 52vw, 440px"
              className="mx-auto h-auto w-[78%] object-contain drop-shadow-[0_26px_40px_rgba(23,23,23,0.2)] sm:w-[72%] lg:w-[68%]"
              priority={false}
            />
          </div>

          <div
            className={`mt-6 flex flex-col items-center justify-center gap-2.5 sm:mt-7 sm:gap-3 transition-all duration-500 ease-out ${
              isVisible
                ? "translate-y-0 opacity-100 delay-200"
                : "translate-y-2 opacity-0"
            }`}
          >
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download PitchBuddy on the App Store"
                className="group relative inline-flex rounded-[0.8rem] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.01] hover:drop-shadow-[0_18px_30px_rgba(30,26,36,0.32)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fff7ea]"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-1 -z-10 rounded-[1rem] bg-primary/35 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
                />
                <Image
                  src="/download-on-app-store.svg"
                  alt="Download on the App Store"
                  width={135}
                  height={41}
                  className="h-[52px] w-auto rounded-xl"
                  priority={false}
                />
              </Link>
              <Link
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get PitchBuddy on Google Play"
                className="group relative inline-flex rounded-[0.8rem] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.01] hover:drop-shadow-[0_18px_30px_rgba(30,26,36,0.32)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fff7ea]"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-1 -z-10 rounded-[1rem] bg-primary/35 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
                />
                <Image
                  src="/download-on-google-store.svg"
                  alt="Get it on Google Play"
                  width={135}
                  height={41}
                  className="h-[52px] w-auto rounded-xl"
                  priority={false}
                />
              </Link>
            </div>
            <p className="text-[0.78rem] font-medium leading-5 text-foreground/55 sm:text-xs">
              Free download • iOS and Android
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
